import { logger } from '@/utils';
import {
  type PinpointMappingType,
  PinpointMultipleFieldEnum,
  type PinpointMultipleMappingType,
} from '../models/pinpoint';

type PinpointType = PinpointMappingType | null;
const PINPOINT_FIELD_KEY_SEPARATOR = '_';

type ModeType =
  | { mode?: 'default'; refsMap?: never }
  | {
      mode: 'startMatchCheck';
      refsMap: Record<string, any>;
    };

type Args = {
  pinpoint: PinpointType;
} & ModeType;

export const getScrollTargetPinpointFieldKey = ({
  pinpoint,
  mode = 'default',
  refsMap,
}: Args): string | undefined => {
  const field = pinpoint?.field;
  if (!field) {
    return;
  }

  const parsedMultipleField = PinpointMultipleFieldEnum.safeParse(field);
  if (parsedMultipleField.success) {
    const { path, field } = pinpoint as PinpointMultipleMappingType;
    switch (field) {
      case 'rs.policy':
      case 'rs.skippedReview.policy': {
        const id = path.policy;
        return [field, id].join(PINPOINT_FIELD_KEY_SEPARATOR);
      }
      case 'rs.legal-bases':
      case 'ss.legal-bases':
      case 'ks.standard.legal-bases': {
        const id = path.legalBasis;
        return `${field}_${id}`;
      }
      case 'rs.plan':
      case 'ss.plan':
      case 'ks.standard.plan': {
        const id = path.plan;
        return [field, id].join(PINPOINT_FIELD_KEY_SEPARATOR);
      }
      case 'rs.subsidy-rate-and-so-on': {
        const id = path.subsidyRate;
        return [field, id].join(PINPOINT_FIELD_KEY_SEPARATOR);
      }
      case 'rs.budget-and-execution':
      case 'rs.skippedReview.budget-and-execution': {
        const id = path.expenditureCart;
        return [field, id].join(PINPOINT_FIELD_KEY_SEPARATOR);
      }
      case 'rs.payment-group':
      case 'ss.payment-group-row':
      case 'ks.standard.payment-group-row': {
        const id = path.paymentGroup;
        return [field, id].join(PINPOINT_FIELD_KEY_SEPARATOR);
      }
      case 'rs.payment-amount-breakdown-row':
      case 'ss.payment-amount-breakdown-row':
      case 'ks.standard.payment-amount-breakdown-row': {
        return [field, path.paymentGroup, path.paymentId].join(
          PINPOINT_FIELD_KEY_SEPARATOR,
        );
      }
      case 'rs.payment-contractor-row':
      case 'ss.payment-contractor-row': {
        const id = path.contractorPaymentId;
        return [field, id].join(PINPOINT_FIELD_KEY_SEPARATOR);
      }
      case 'rs.summary-of-publishing': {
        const id = path.summaryAndResponseProcess;
        return [field, id].join(PINPOINT_FIELD_KEY_SEPARATOR);
      }
      case 'rs.others-indicated-item': {
        const id = path.otherIndicatedItemAndResponseReason;
        return [field, id].join(PINPOINT_FIELD_KEY_SEPARATOR);
      }
      case 'rs.activity':
      case 'ss.activity':
      case 'ks.standard.activity':
      case 'ks.local-government.activity': {
        const targetKey = [field, path.activityId, path.routeIndicatorId].join(
          PINPOINT_FIELD_KEY_SEPARATOR,
        );
        return getAllMatchOrStartMatchPinpointKey({
          mode,
          refsMap,
          targetKey,
        });
      }
      case 'rs.indicator-title':
      case 'rs.indicator-description':
      case 'rs.activity-and-result-target':
      case 'rs.achievement-for-qualitative-outcome':
      case 'rs.indicator-setting-reason':
      case 'rs.reason-for-setting-qualitative-outcome':
      case 'rs.reason-why-outcomes-cannot-be-multiple':
      case 'rs.connection-to-next-outcome':
      case 'ss.indicator-title':
      case 'ss.indicator-description':
      case 'ss.activity-and-result-target':
      case 'ss.achievement-for-qualitative-outcome':
      case 'ss.indicator-setting-reason':
      case 'ss.reason-for-setting-qualitative-outcome':
      case 'ss.reason-why-outcomes-cannot-be-multiple':
      case 'ss.connection-to-next-outcome':
      case 'ks.local-government.indicator-title':
      case 'ks.local-government.indicator-description':
      case 'ks.local-government.activity-and-result-target':
      case 'ks.local-government.achievement-for-qualitative-outcome':
      case 'ks.local-government.indicator-setting-reason':
      case 'ks.local-government.reason-for-setting-qualitative-outcome':
      case 'ks.local-government.reason-why-outcomes-cannot-be-multiple':
      case 'ks.local-government.connection-to-next-outcome':
      case 'ks.standard.indicator-title':
      case 'ks.standard.indicator-description':
      case 'ks.standard.activity-and-result-target':
      case 'ks.standard.achievement-for-qualitative-outcome':
      case 'ks.standard.indicator-setting-reason':
      case 'ks.standard.reason-for-setting-qualitative-outcome':
      case 'ks.standard.reason-why-outcomes-cannot-be-multiple':
      case 'ks.standard.connection-to-next-outcome': {
        const targetKey = [
          field,
          path.activityIndicatorId,
          path.routeIndicatorId,
        ].join(PINPOINT_FIELD_KEY_SEPARATOR);
        return getAllMatchOrStartMatchPinpointKey({
          mode,
          refsMap,
          targetKey,
        });
      }
      case 'ks.standard.fund-business-table': {
        const id = path.fundBusinessId;
        return [field, id].join(PINPOINT_FIELD_KEY_SEPARATOR);
      }
      default: {
        const check: never = field;
        logger.error('Invalid pinpoint field', {
          check,
        });
        return undefined;
      }
    }
  }

  return field;
};

const getAllMatchOrStartMatchPinpointKey = ({
  mode,
  refsMap,
  targetKey,
}: Omit<Args, 'pinpoint'> & { targetKey: string }) => {
  const isMatchPinpointKey = refsMap?.[targetKey];
  if (!mode || mode === 'default' || isMatchPinpointKey) {
    return targetKey;
  }

  const matchPinpointKey = Object.keys(refsMap ?? {}).find((refKey) =>
    refKey.startsWith(targetKey),
  );
  return matchPinpointKey ?? '';
};
